import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getPageContent, updatePageContent,
  getCmsSettings, updateCmsSettings,
  getCmsCustomization, updateCmsCustomization,
  listAdminBlogs, listAdminTestimonials, listTeamMembers, listWebsiteCourses,
  uploadCmsImage,
} from '../../utils/cmsAdminApi';
import {
  normalizeCustomization, customizationPayload, validateCustomization,
} from './cmsSiteModel';

// ─── useCmsWorkspace — everything CMS Management edits, in one place ─────
//
// Every tab edits a draft held here, so switching tabs never loses work and
// one save bar can save it all. Three stores are saved together:
//   • page content      PUT  /cms/page-content          (the whole object)
//   • customisation     PUT  /institute/cms-customization
//   • colour mode       PATCH /institute/cms-settings   (layoutBrandingMode)
// Going live and switching layout are deliberate one-off actions with their
// own confirmation, so they apply immediately instead of joining the draft.

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export default function useCmsWorkspace({ notify }) {
  const [loading, setLoading] = useState(true);
  // A store that failed to load must not be edited: its draft would start
  // empty, and saving it would overwrite everything the website has.
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const [content, setContent] = useState({});
  const [savedContent, setSavedContent] = useState({});
  const [customization, setCustomization] = useState(() => normalizeCustomization());
  const [savedCustomization, setSavedCustomization] = useState(() => normalizeCustomization());
  const [settings, setSettings] = useState({ cmsEnabled: false, layoutPreset: 'default', layoutBrandingMode: 'preset' });
  const [savedBrandingMode, setSavedBrandingMode] = useState('preset');
  const [stats, setStats] = useState({
    totalBlogs: 0, publishedBlogs: 0, totalTestimonials: 0, visibleTeam: 0,
  });
  const [courses, setCourses] = useState([]);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [pageRes, settingsRes, customRes, blogsRes, publishedRes, testRes, coursesRes, teamRes] = await Promise.all([
      getPageContent().catch(() => null),
      getCmsSettings().catch(() => null),
      getCmsCustomization().catch(() => null),
      listAdminBlogs({ page: 1, limit: 1 }).catch(() => null),
      listAdminBlogs({ page: 1, limit: 1, status: 'published' }).catch(() => null),
      listAdminTestimonials().catch(() => null),
      listWebsiteCourses().catch(() => null),
      listTeamMembers().catch(() => null),
    ]);
    if (!pageRes || !settingsRes || !customRes) {
      setLoadFailed(true);
      setLoading(false);
      return;
    }
    setLoadFailed(false);

    const pageContent = pageRes?.data || {};
    setContent(pageContent);
    setSavedContent(pageContent);

    const custom = normalizeCustomization(customRes || {}, pageContent.global || {});
    setCustomization(custom);
    setSavedCustomization(custom);

    const mode = settingsRes?.layoutBrandingMode || 'preset';
    setSettings({
      cmsEnabled: !!settingsRes?.cmsEnabled,
      layoutPreset: settingsRes?.layoutPreset || 'default',
      layoutBrandingMode: mode,
    });
    setSavedBrandingMode(mode);

    setStats({
      totalBlogs: blogsRes?.pagination?.total ?? 0,
      publishedBlogs: publishedRes?.pagination?.total ?? 0,
      totalTestimonials: (testRes?.data || []).length,
      // The checklist asks whether the website has a team on it, so a profile
      // saved but hidden does not count.
      visibleTeam: (teamRes?.data || []).filter((m) => m.isActive !== false).length,
    });
    setCourses(coursesRes?.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Drafts ────────────────────────────────────────────────────────────
  const setPageField = useCallback((pageKey, key, value) => {
    setContent((prev) => ({ ...prev, [pageKey]: { ...(prev[pageKey] || {}), [key]: value } }));
  }, []);

  const setCustomizationField = useCallback((section, key, value) => {
    setCustomization((prev) => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  }, []);

  const setBrandingMode = useCallback((mode) => {
    setSettings((prev) => ({ ...prev, layoutBrandingMode: mode }));
  }, []);

  /** Per-page SEO suggested by the SEO audit — joins the draft like any edit. */
  const applyPageSeo = useCallback((pageKey, seo) => {
    setContent((prev) => {
      const page = prev[pageKey] || {};
      return { ...prev, [pageKey]: { ...page, seo: { ...(page.seo || {}), ...seo } } };
    });
  }, []);

  const dirty = useMemo(() => ({
    content: !same(content, savedContent),
    customization: !same(customization, savedCustomization),
    brandingMode: settings.layoutBrandingMode !== savedBrandingMode,
  }), [content, savedContent, customization, savedCustomization, settings.layoutBrandingMode, savedBrandingMode]);
  const isDirty = dirty.content || dirty.customization || dirty.brandingMode;

  // Which tabs hold the unsaved changes — for the save bar and the tab list.
  // `global` is the Header & Footer tab's; theme-3's `t3_global` is a page.
  const dirtyTabs = useMemo(() => {
    const tabs = new Set();
    new Set([...Object.keys(content), ...Object.keys(savedContent)]).forEach((key) => {
      if (!same(content[key], savedContent[key])) tabs.add(key === 'global' ? 'header-footer' : 'pages');
    });
    if (!same(customization.navbar, savedCustomization.navbar)
      || !same(customization.footer, savedCustomization.footer)) tabs.add('header-footer');
    if (!same(customization.theme, savedCustomization.theme)
      || !same(customization.customCode, savedCustomization.customCode)
      || dirty.brandingMode) tabs.add('design');
    return tabs;
  }, [content, savedContent, customization, savedCustomization, dirty.brandingMode]);

  const errors = useMemo(() => validateCustomization(customization), [customization]);
  const errorCount = Object.keys(errors).length;

  // ── Save / discard ────────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (errorCount) {
      notify(`Fix ${errorCount === 1 ? 'the highlighted field' : `${errorCount} highlighted fields`} before saving.`, 'error');
      return false;
    }
    setSaving(true);
    const jobs = [];
    if (dirty.content) {
      const snapshot = content;
      jobs.push(updatePageContent(snapshot).then(() => setSavedContent(snapshot)));
    }
    if (dirty.customization) {
      const snapshot = customization;
      jobs.push(updateCmsCustomization(customizationPayload(snapshot)).then(() => setSavedCustomization(snapshot)));
    }
    if (dirty.brandingMode) {
      const mode = settings.layoutBrandingMode;
      jobs.push(updateCmsSettings({ layoutBrandingMode: mode }).then(() => setSavedBrandingMode(mode)));
    }
    const results = await Promise.allSettled(jobs);
    setSaving(false);
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length) {
      const message = failed[0].reason?.response?.data?.message;
      notify(message || 'Some changes could not be saved. Nothing you entered was lost — try again.', 'error');
      return false;
    }
    notify('Saved. Your website updates within a minute.');
    return true;
  }, [errorCount, dirty, content, customization, settings.layoutBrandingMode, notify]);

  const discard = useCallback(() => {
    setContent(savedContent);
    setCustomization(savedCustomization);
    setSettings((prev) => ({ ...prev, layoutBrandingMode: savedBrandingMode }));
  }, [savedContent, savedCustomization, savedBrandingMode]);

  // ── Immediate settings ────────────────────────────────────────────────
  const setCmsEnabled = useCallback(async (next) => {
    setSettingsBusy(true);
    try {
      await updateCmsSettings({ cmsEnabled: next });
      setSettings((prev) => ({ ...prev, cmsEnabled: next }));
      notify(next ? 'Your website is live.' : 'Your website is switched off.');
    } catch {
      notify('Could not change the website status. Try again.', 'error');
    } finally {
      setSettingsBusy(false);
    }
  }, [notify]);

  const setLayoutPreset = useCallback(async (next) => {
    setSettingsBusy(true);
    try {
      const res = await updateCmsSettings({ layoutPreset: next });
      setSettings((prev) => ({ ...prev, layoutPreset: res?.layoutPreset || next }));
      notify('Layout switched. Your website updates within a minute.');
    } catch {
      notify('Could not switch the layout. Try again.', 'error');
    } finally {
      setSettingsBusy(false);
    }
  }, [notify]);

  // ── Customisation images ──────────────────────────────────────────────
  // Each image is stored as an uploaded file's storage key OR a pasted URL,
  // plus a preview URL the API returns for the key. `imageSlot` binds one to
  // an <ImageField>.
  const imageSlot = useCallback((section, base, kind = 'page-media') => {
    const slotId = `${section}.${base}`;
    const values = customization[section];
    const keyName = `${base}S3Key` in values ? `${base}S3Key` : `${base}Key`;
    const set = (patch) => setCustomization((prev) => ({ ...prev, [section]: { ...prev[section], ...patch } }));
    return {
      previewUrl: values[`${base}ExternalUrl`].trim() || values[`${base}PreviewUrl`],
      urlValue: values[`${base}ExternalUrl`],
      busy: uploadingSlot === slotId,
      onUrlChange: (url) => set({
        [`${base}ExternalUrl`]: url,
        ...(url.trim() ? { [keyName]: '', [`${base}PreviewUrl`]: '' } : {}),
      }),
      onRemove: () => set({ [keyName]: '', [`${base}ExternalUrl`]: '', [`${base}PreviewUrl`]: '' }),
      onFile: async (file) => {
        setUploadingSlot(slotId);
        try {
          const res = await uploadCmsImage(file, kind);
          if (!res?.key) throw new Error('upload failed');
          set({ [keyName]: res.key, [`${base}ExternalUrl`]: '', [`${base}PreviewUrl`]: res.url || '' });
        } catch (err) {
          notify(err?.response?.data?.message || 'The image could not be uploaded.', 'error');
        } finally {
          setUploadingSlot('');
        }
      },
    };
  }, [customization, uploadingSlot, notify]);

  return {
    loading,
    loadFailed,
    saving,
    content,
    setPageField,
    applyPageSeo,
    customization,
    setCustomizationField,
    imageSlot,
    settings,
    settingsBusy,
    setCmsEnabled,
    setLayoutPreset,
    setBrandingMode,
    stats,
    courses,
    dirty,
    isDirty,
    dirtyTabs,
    errors,
    save,
    discard,
    reload: load,
  };
}
